import { useMemo } from "react";

import { CHART_RADIUS_RATIO } from "./constants";
import { buildAxisLines, buildDataPointCoordinates, buildGridPaths, buildPolygonPoints } from "./helpers";
import type { AxisLine, Point, RadarDatum } from "./types";

type RadarChartGeometry = {
  cx: number;
  cy: number;
  polygonPoints: string;
  secondaryPolygonPoints: string | undefined;
  dataPoints: Point[];
  secondaryDataPoints: Point[] | undefined;
  gridPaths: string[];
  axisLines: AxisLine[];
};

const EMPTY_GEOMETRY: RadarChartGeometry = {
  cx: 0,
  cy: 0,
  polygonPoints: "",
  secondaryPolygonPoints: undefined,
  dataPoints: [],
  secondaryDataPoints: undefined,
  gridPaths: [],
  axisLines: [],
};

const useRadarChartGeometry = (
  data: RadarDatum[],
  size: number,
  maxValue: number,
  gridLevels: number,
  secondaryData?: RadarDatum[]
): RadarChartGeometry => {
  return useMemo(() => {
    if (!data.length || size === 0) {
      return EMPTY_GEOMETRY;
    }

    const cx = size / 2;
    const cy = size / 2;
    const radius = (size / 2) * CHART_RADIUS_RATIO;
    const count = data.length;

    const hasSecondary = secondaryData?.length === count;

    return {
      cx,
      cy,
      polygonPoints: buildPolygonPoints(data, cx, cy, radius, maxValue),
      secondaryPolygonPoints: hasSecondary
        ? buildPolygonPoints(secondaryData, cx, cy, radius, maxValue)
        : undefined,
      dataPoints: buildDataPointCoordinates(data, cx, cy, radius, maxValue),
      secondaryDataPoints: hasSecondary
        ? buildDataPointCoordinates(secondaryData, cx, cy, radius, maxValue)
        : undefined,
      gridPaths: buildGridPaths(count, gridLevels, cx, cy, radius).reverse(),
      axisLines: buildAxisLines(count, cx, cy, radius),
    };
  }, [data, secondaryData, maxValue, size, gridLevels]);
};

export default useRadarChartGeometry;
